import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, CheckCircle2, EyeOff, Pencil, Plus, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, resolveImage } from "@/lib/api";

export default function AdminBlog() {
  const [posts, setPosts] = useState(null);

  const load = () => api.get("/admin/blog").then(({ data }) => setPosts(data)).catch(() => setPosts([]));
  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (p) => {
    if (!window.confirm(`Supprimer l'article « ${p.title} » ? Cette action est définitive.`)) return;
    try {
      await api.delete(`/admin/blog/${p.slug}`);
      toast.success("Article supprimé");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleToggleStatus = async (p) => {
    const next = p.status === "draft" ? "published" : "draft";
    try {
      await api.put(`/admin/blog/${p.slug}/status`, { status: next });
      toast.success(next === "published" ? "Article publié" : "Article repassé en brouillon");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-blog" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-semibold text-bark">Gazette</h1>
          <p className="mt-1 text-sm text-bark-soft">Les articles publiés apparaissent immédiatement sur la page Gazette.</p>
        </div>
        <Link
          to="/admin/gazette/nouveau"
          data-testid="new-post-button"
          className="flex items-center gap-2 rounded-full bg-amber-deep px-5 py-2.5 text-sm font-medium text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-95"
        >
          <Plus size={16} /> Nouvel article
        </Link>
      </div>

      {!posts ? (
        <p data-testid="blog-loading" className="py-16 text-center font-serif text-xl text-bark-soft">Chargement…</p>
      ) : posts.length === 0 ? (
        <p data-testid="blog-empty" className="py-16 text-center font-serif text-xl text-bark-soft">Aucun article. Écrivez votre première gazette !</p>
      ) : (
        <div className="space-y-4">
          {posts.map((p) => (
            <div key={p.slug} data-testid={`blog-row-${p.slug}`} className="flex items-center gap-4 rounded-2xl border border-amber-800/15 bg-white p-5 shadow-sm">
              {p.image_url ? (
                <img src={resolveImage(p.image_url)} alt="" className="h-16 w-16 shrink-0 rounded-xl border border-amber-900/10 object-cover" />
              ) : (
                <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-sand text-stone-mute">
                  <BookOpen size={22} />
                </span>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-serif text-lg font-semibold text-bark">{p.title}</p>
                  {p.status === "draft" ? (
                    <span data-testid={`blog-status-${p.slug}`} className="rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-deep">Brouillon</span>
                  ) : (
                    <span data-testid={`blog-status-${p.slug}`} className="rounded-full bg-sage-light px-2.5 py-0.5 text-xs font-medium text-sage">Publié</span>
                  )}
                  {p.source === "ai_agent" && (
                    <span className="flex items-center gap-1 rounded-full bg-honey/15 px-2.5 py-0.5 text-xs font-medium text-honey"><Sparkles size={11} /> IA</span>
                  )}
                </div>
                <p className="truncate text-sm text-bark-soft">{p.excerpt}</p>
                <p className="mt-1 text-xs uppercase tracking-[0.12em] text-stone-mute">
                  {p.tag} · {p.date ? new Date(p.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : ""} · {p.read_time}
                </p>
              </div>
              <div className="flex shrink-0 gap-2">
                <button
                  data-testid={`toggle-status-${p.slug}`}
                  onClick={() => handleToggleStatus(p)}
                  className={`flex h-9 w-9 items-center justify-center rounded-full border transition-colors ${p.status === "draft" ? "border-sage/40 text-sage hover:bg-sage-light" : "border-amber-800/20 text-bark-soft hover:border-amber-deep hover:text-amber-deep"}`}
                  aria-label={p.status === "draft" ? `Publier ${p.title}` : `Repasser ${p.title} en brouillon`}
                  title={p.status === "draft" ? "Publier" : "Repasser en brouillon"}
                >
                  {p.status === "draft" ? <CheckCircle2 size={15} /> : <EyeOff size={15} />}
                </button>
                <Link
                  to={`/admin/gazette/${p.slug}`}
                  data-testid={`edit-post-${p.slug}`}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-amber-800/20 text-bark-soft transition-colors hover:border-amber-deep hover:text-amber-deep"
                  aria-label={`Modifier ${p.title}`}
                >
                  <Pencil size={15} />
                </Link>
                <button
                  data-testid={`delete-post-${p.slug}`}
                  onClick={() => handleDelete(p)}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-terra/30 text-terra transition-colors hover:bg-terra/10"
                  aria-label={`Supprimer ${p.title}`}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
